import React, { useState, useEffect } from 'react'
import { Flashcard, ReviewResponse, Quality } from '../types'
import { apiClient } from '../api/client'
import { useAuth } from '../contexts/AuthContext'
import FlashcardComponent from './FlashcardComponent'
import './ReviewSession.css'

interface ReviewSessionProps {
  deckId?: string
  sessionType?: 'mixed' | 'due_only' | 'new_only'
}

const ReviewSession: React.FC<ReviewSessionProps> = ({ deckId, sessionType = 'mixed' }) => {
  const { user } = useAuth()
  const [cards, setCards] = useState<Flashcard[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isReviewing, setIsReviewing] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [reviewResults, setReviewResults] = useState<ReviewResponse[]>([])
  const [showResult, setShowResult] = useState(false)

  useEffect(() => {
    startSession()
  }, [deckId, sessionType])

  const startSession = async () => {
    setIsLoading(true)
    setError(null)

    try {
      if (!user || !user.user_id) {
        throw new Error('ユーザーが認証されていません')
      }

      const response = await apiClient.getReviewSession(deckId, sessionType)
      // Backend returns data directly: { session_id, cards, total_cards, due_cards, new_cards }
      const sessionData = response.data || response
      if (sessionData && typeof sessionData === 'object' && 'cards' in sessionData) {
        setCards(sessionData.cards || [])
        setCurrentIndex(0)
        setIsReviewing(true)
        setReviewResults([])
        setShowResult(false)
      } else {
        setError('復習セッションを開始できませんでした')
      }
    } catch (err: any) {
      console.error('Error starting review session:', err)
      setError(err.message || '復習セッションを開始できませんでした')
    } finally {
      setIsLoading(false)
    }
  }

  const handleReview = async (cardId: string, quality: Quality) => {
    try {
      if (!user || !user.user_id) {
        throw new Error('ユーザーが認証されていません')
      }

      const response = await apiClient.reviewCard({
        card_id: cardId,
        quality
      })

      if (response.data) {
        const result = response.data
        setReviewResults(prev => [...prev, result])

        // Move to next card or show results
        if (currentIndex < cards.length - 1) {
          setCurrentIndex(prev => prev + 1)
        } else {
          setShowResult(true)
          setIsReviewing(false)
        }
      } else {
        setError('復習結果を送信できませんでした')
      }
    } catch (err: any) {
      console.error('Error submitting review:', err)
      setError(err.message || '復習結果を送信できませんでした')
    }
  }

  const handleNextCard = () => {
    if (currentIndex < cards.length - 1) {
      setCurrentIndex(prev => prev + 1)
    }
  }

  const handlePrevCard = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1)
    }
  }

  const handleRestartSession = () => {
    setShowResult(false)
    setIsReviewing(true)
    setCurrentIndex(0)
    setReviewResults([])
    setError(null)
  }

  const handleCloseSession = () => {
    // Navigate back to vocabulary or progress page
    window.history.back()
  }

  if (isLoading) {
    return (
      <div className="review-session">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>復習セッションを準備中...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="review-session">
        <div className="error-container">
          <h2>セッションエラー</h2>
          <p>{error}</p>
          <div className="error-actions">
            <button onClick={startSession} className="retry-btn">
              再試行
            </button>
            <button onClick={handleCloseSession} className="close-btn">
              閉じる
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (showResult) {
    return (
      <div className="review-session">
        <div className="session-completed">
          <div className="completion-header">
            <h2>セッション完了！ 🎉</h2>
            <p>復習セッションを完了しました、お疲れさまでした</p>
          </div>

          <div className="session-stats">
            <div className="stat-card">
              <span className="stat-label">合計カード数</span>
              <span className="stat-value">{cards.length}</span>
            </div>
            <div className="stat-card">
              <span className="stat-label">復習済み</span>
              <span className="stat-value">{reviewResults.length}</span>
            </div>
            <div className="stat-card">
              <span className="stat-label">正答率</span>
              <span className="stat-value">
                {reviewResults.length > 0
                  ? Math.round(
                      (reviewResults.filter(r => r.next_review.quality !== Quality.AGAIN).length /
                       reviewResults.length) *
                        100
                    )
                  : 0}%
              </span>
            </div>
          </div>

          <div className="session-actions">
            <button onClick={handleRestartSession} className="restart-btn">
              もう一度復習
            </button>
            <button onClick={handleCloseSession} className="close-btn">
              セッションを閉じる
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (!isReviewing || cards.length === 0) {
    return (
      <div className="review-session">
        <div className="no-cards-container">
          <h2>カードがありません</h2>
          <p>
            {sessionType === 'due_only'
              ? '現在復習対象のカードはありません。'
              : sessionType === 'new_only'
              ? '復習できる新しいカードはありません。'
              : 'このセッションには対象カードがありません。'}
          </p>
          <div className="no-cards-actions">
            <button onClick={handleCloseSession} className="back-btn">
              単語リストへ戻る
            </button>
          </div>
        </div>
      </div>
    )
  }

  const currentCard = cards[currentIndex]
  const progressPercentage = ((currentIndex + 1) / cards.length) * 100

  return (
    <div className="review-session">
      <div className="session-header">
        <div className="session-info">
          <h2>復習セッション</h2>
          <p>
            {sessionType === 'due_only' && '期限切れカードを復習中'}
            {sessionType === 'new_only' && '新しいカードを学習中'}
            {sessionType === 'mixed' && 'ミックス復習セッション'}
          </p>
        </div>
        <div className="session-progress">
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${progressPercentage}%` }}
            ></div>
          </div>
          <span className="progress-text">
            {currentIndex + 1} / {cards.length}
          </span>
        </div>
      </div>

      <div className="flashcard-wrapper">
        <FlashcardComponent
          card={currentCard}
          onReview={handleReview}
          isFlipped={false}
        />
      </div>

      <div className="navigation-controls">
        <button
          onClick={handlePrevCard}
          disabled={currentIndex === 0}
          className="nav-btn prev-btn"
        >
          前へ
        </button>
        <span className="card-counter">
          {currentIndex + 1} / {cards.length}
        </span>
        <button
          onClick={handleNextCard}
          disabled={currentIndex === cards.length - 1}
          className="nav-btn next-btn"
        >
          次へ
        </button>
      </div>

      <div className="session-footer">
        <button onClick={handleCloseSession} className="session-close-btn">
          セッションを終了
        </button>
      </div>
    </div>
  )
}

export default ReviewSession